"use client"

import React, { useState } from "react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { PasswordInput } from "../../components/ui/password-input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { useNavigate, Link } from "react-router-dom"
import { toast } from "react-toastify"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { useAuth } from "../../contexts/AuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import { Loader2 } from "lucide-react"

export default function LoginPage() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()
  const { t } = useLanguage()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const validateForm = () => {
    if (!formData.username.trim()) {
      toast.error(t('login.enterUsername'))
      return false
    }

    if (!formData.password.trim()) {
      toast.error(t('login.enterPassword'))
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const success = await login(formData.username, formData.password)
      
      if (success) {
        toast.success(t('login.success'))
        
        // 检查是否有待处理的提示词
        const pendingPrompt = localStorage.getItem('pending_prompt')
        if (pendingPrompt) {
          // 清除待处理的提示词
          localStorage.removeItem('pending_prompt')
          // 跳转到AI编辑页面并带上提示词
          navigate(`/agent?input=${encodeURIComponent(pendingPrompt)}`)
        } else {
          // 没有待处理的提示词，跳转到首页
          navigate("/")
        }
      } else {
        toast.error(t('login.loginFailed'))
      }
    } catch (error: any) {
      toast.error(t('login.loginError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('login.title')}</CardTitle>
            <CardDescription>
              {t('login.welcome')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('login.username')}
                </label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder={t('login.usernamePlaceholder')}
                  className="mt-1"
                />
              </div>
              <PasswordInput
                id="password"
                name="password"
                label={t('login.password')}
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('login.passwordPlaceholder')}
              />
              <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  t('login.signIn')
                )}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                {t('login.noAccount')}{" "}
                <Link to="/register" className="text-blue-800 hover:text-blue-900 font-medium">
                  {t('login.registerNow')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </>
  )
}
